'use client'

import { useState, useEffect, useRef } from 'react'
import { TickerItem } from '../../types'
import { ConnectorManager } from '../../ConnectorManager'

interface UseZerodhaWebSocketProps {
  onTickerUpdate: (tickers: TickerItem[]) => void
}

export function useZerodhaWebSocket({ onTickerUpdate }: UseZerodhaWebSocketProps) {
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const connect = () => {
    try {
      const zerodhaConnector = ConnectorManager.getInstance().getConnector('zerodha')
      if (!zerodhaConnector) {
        throw new Error('Zerodha connector not found or not connected')
      }

      // Get the WebSocket URL from the connector
      const wsUrl = zerodhaConnector.getWebSocketUrl()
      
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close()
      }

      // Create new WebSocket connection
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        setIsConnected(true)
        setError(null)
        console.log('Connected to Zerodha WebSocket')

        // Subscribe to tickers
        const subscribeMessage = {
          type: 'subscribe',
          tokens: ['NSE:NIFTY-50'] // Add more tokens as needed
        }
        ws.send(JSON.stringify(subscribeMessage))
      }

      ws.onmessage = async (event) => {
        let message: string;
        if (event.data instanceof Blob) {
          message = await event.data.text();
        } else {
          message = event.data;
        }
      
        // Check if the message is non-empty
        if (!message.trim()) {
          return; // Skip processing if message is empty
        }
      
        try {
          const data = JSON.parse(message);
          if (data.type === 'ticks') {
            // Transform the data into TickerItem format
            const tickers: TickerItem[] = data.ticks.map((tick: any) => ({
              symbol: tick.tradingsymbol,
              originalSymbol: tick.tradingsymbol, // Add originalSymbol property
              exchange: tick.exchange || 'NSE', // Add exchange property with default
              price: tick.last_price,
              priceChangePercent: tick.change,
              volume: tick.volume
            }));
            onTickerUpdate(tickers);
          }
        } catch (err) {
          console.error('Error processing WebSocket message:', err);
        }
      };
      
      

      ws.onerror = (event) => {
        console.error('WebSocket error:', event)
        setError('WebSocket connection error')
      }

      ws.onclose = () => {
        setIsConnected(false)
        console.log('WebSocket connection closed')
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...')
          connect()
        }, 5000)
      }

      wsRef.current = ws
    } catch (err) {
      console.error('Error establishing WebSocket connection:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  useEffect(() => {
    connect()

    return () => {
      // Cleanup function
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [])

  return { isConnected, error }
}